import type { ComponentType } from 'react'
import { lazy } from 'react'

const RELOAD_KEY = 'pre-qualify:chunk-reload-at'
const RETRY_AFTER_MS = 60_000

const isChunkLoadError = (error: unknown): boolean => {
    if (!(error instanceof Error)) return false
    const message = error.message || ''
    return (
        message.includes('Failed to fetch dynamically imported module') ||
        message.includes('error loading dynamically imported module') ||
        message.includes('Importing a module script failed') ||
        message.includes('is not a valid JavaScript MIME type') ||
        message.includes('Unable to preload CSS')
    )
}

// Wrap React.lazy so that a stale-chunk import (typical after a redeploy) triggers
// exactly one full page reload per RETRY_AFTER_MS window, fetching a fresh
// index.html with current chunk hashes. The timestamped guard prevents reload
// loops while still allowing recovery if another deploy happens later in the
// same session.
export const lazyWithRetry = <T extends ComponentType<any>>(
    factory: () => Promise<{ default: T }>,
) => {
    return lazy(async () => {
        try {
            return await factory()
        } catch (error) {
            if (!isChunkLoadError(error)) throw error

            const lastReloadAt = Number(sessionStorage.getItem(RELOAD_KEY) ?? 0)
            if (Date.now() - lastReloadAt < RETRY_AFTER_MS) throw error

            sessionStorage.setItem(RELOAD_KEY, String(Date.now()))
            window.location.reload()

            return new Promise<{ default: T }>(() => {
                // Stall the Suspense boundary while the reload happens.
            })
        }
    })
}
